import React, { useEffect, useMemo, useRef, useState } from 'react';
import Column from '@components/column';
import Image from '@components/image';
import Button from '@components/button';
import Pagination from '@components/pagination';
import Field from '../form/field';
import { useForm } from 'react-hook-form';
import { ReactComponent as SearchIcon } from '@svgs/search.svg';
import { setAnswersByQuery, getGladlyAnswersByType, getGladlySections } from './data';
import FaqAccordion from './accordion';
import { useIsMobile } from '@src/utils/general';

const HelpCenter = ({ heading, description, backgroundImage, defaultSettings }) => {
  const formRef = useRef();
  const { register, handleSubmit } = useForm();

  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(sections[0]);
  const [answers, setAnswers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState({
    query: '',
    active: false,
  });

  useEffect(() => {
    setSearch({
      query: '',
      active: false,
    });
    getGladlySections().then(res => setSections(res));
  }, []);

  useEffect(() => {
    if (search.active) {
      setAnswersByQuery(search, setAnswers, setSelectedSection);
    } else {
			setSelectedSection(sections[0])
		}
  }, [search.query]);

  useEffect(() => {
    setSelectedSection(sections[0]);
  }, [sections]);

  useEffect(() => {
    if (selectedSection?.answerIds) {
      getGladlyAnswersByType(selectedSection?.answerIds, 'id').then(res => setAnswers(res));
    }
    setCurrentPage(1);
  }, [selectedSection]);

  const faqsPerPage = useIsMobile() ? 5 : 9;

  const totalPages = Math.ceil(answers?.length / faqsPerPage);

  const answersToDisplay = useMemo(() => {
    const startIndex = (currentPage - 1) * faqsPerPage;
    const endIndex = startIndex + faqsPerPage;

    return answers?.slice(startIndex, endIndex);
  }, [answers, currentPage]);

  const onPageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const onSubmit = ({ search: query }) => {
    if (query) {
      setSearch({
        query,
        active: true,
      });
    }
  };

  return (
    <div className='grid grid-cols-12 gap-x-5 gap-y-10'>
      <div className='col-span-12'>
        <form className='flex gap-x-2.5' onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <div className='w-full flex border-1 border-solid border-gray rounded-xl pl-10 px-5 py-2.5 items-center gap-x-2.5 md:gap-x-5 group'>
            <Field
              name="search"
              type="search"
              placeholder="How can we help you?"
              register={register}
              className="p-0 justify-center search-field shrink w-[inherit]"
              inputPostfix={"help-center"}
            />
          </div>
          <Button
            theme="btn-secondary"
            buttonType={'submit'}
            arrow={'none'}
            ariaLabel={'Search'}
          >
            <SearchIcon className="w-fit flex flex-shrink-0" />
          </Button>
        </form>
      </div>
      <Column colorSettings={defaultSettings?.colorSettings} className={'relative col-span-12 md:col-span-6'} innerClassName={'relative aspect-1 h-fit min-h-[600px] xl:min-h-fit justify-between'}>
        <Image
          image={backgroundImage}
          objectFit={'contain'}
          objectPosition={'right bottom'}
          className={'!absolute bottom-0 right-0 rounded-br-xl w-fit max-w-60'}
        />
        <div className='z-10 flex flex-col gap-y-10'>
          <h1>{heading}</h1>
          <div className='flex flex-col gap-y-2.5'>
            {search.active &&
              <Button
                arrow={'none'}
                theme={selectedSection === null ? 'bg-soft-black text-white border-soft-black border-1' : 'btn-transparent-dark hover:bg-soft-black hover:text-white hover:border-soft-black'}
                className={'text-base font-normal px-3 py-2.5 leading-none'}
                onClick={() => setAnswersByQuery(search, setAnswers, setSelectedSection)}
              >
                <span className='font-medium'>Search:</span>
								<span className='pl-xsmall'>{search.query}</span>
              </Button>
            }
            <div className='flex flex-wrap gap-2.5'>
              {sections.map((section, index) => (
                <Button
                  key={`${section?.header.toLowerCase()} ${index}`}
                  arrow={'none'}
                  theme={selectedSection == section ? 'bg-soft-black text-white border-soft-black border-1' : 'btn-transparent-dark hover:bg-soft-black hover:text-white hover:border-soft-black'}
                  className={'text-base font-normal px-3 py-2.5 leading-none'}
                  onClick={() => setSelectedSection(section)}
                >
                  {section?.header}
                </Button>
              )
              )}
            </div>
          </div>
        </div>
        <p className='z-10 max-w-2/3'>{description}</p>
      </Column>
      <Column noPadding={true} className={'col-span-12 md:col-span-6'} innerClassName={'pb-2.5'}>
        <div className='flex flex-col h-full font-owners'>
          {answersToDisplay.length > 0
            ? answersToDisplay?.map(answer => (
              <FaqAccordion
                key={answer.id}
                question={answer.name}
                answer={answer.bodyHtml}
              />
            ))
            : <div className='h-full flex justify-center items-center'>
              {`No results for ${search.active ? search.query : selectedSection?.header}`}
            </div>
          }
        </div>
        {totalPages > 1 &&
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            isLink={false}
          />
        }
      </Column>
    </div>
  );
};

export default HelpCenter;